import date from "date-and-time";
import { getOpsChatIntakeTiers, formatMonthlyUsd } from "../lib/pricing";

// General Info
export const COMPANY_NAME = "Junoware";
export const FULL_COMPANY_NAME = "Junoware LLC";
export const APP_TYPE = process.env.REACT_APP_TYPE || "landlord";
export const APP_NAME = "LandlordOps";
export const APP_ACTION = APP_NAME;
export const APP_NAME_LOWER = APP_NAME.toLowerCase().replace(/\s+/g, "-");

/**
 * Brand hierarchy (customer-facing SEO and copy):
 * - LandlordOps: company / app name - full OS on every tier (rent, ledgers, vendors, Schedule E, Leo).
 * - Your AI First Responder: Leo AI on the tenant Magic Link (24/7 triage before your phone).
 * - Leo AI: the assistant on the tenant Magic Link (not branded “LeoChat” to users; LeoChat* names in code are dev components).
 * Do not use “OpsChat” in public SEO titles, meta, or schema.
 */
/** Product tagline: titles, manifest, and secondary pages. Homepage document title uses HOME_PAGE_TITLE. */
export const TAGLINE = "Your AI First Responder";
/** Lowercase tagline for use in sentences (e.g. "LandlordOps is your AI first responder"). */
export const TAGLINE_SENTENCE = "your AI first responder";
/**
 * Hero eyebrow (above H1) on marketing pages that reuse these tokens.
 */
export const HERO_EYEBROW =
  "LANDLORDOPS · FULL OS · AI FIRST RESPONDER";
/**
 * Hero subcopy for pages that import HERO_SUBTITLE (e.g. ForSelfManagingLandlords).
 */
export const HERO_SUBTITLE =
  "Leo AI sits between you and tenant noise: a Magic Link for renters, maintenance intake in chat, and emergency escalation when it is real. Every plan includes rent collection, Schedule E, vendors, and ledgers - tiers differ only by door count.";
/**
 * Default meta / org schema / global SoftwareApplication description.
 */
export const SEO_DESCRIPTION = `${APP_NAME} is ${TAGLINE_SENTENCE}. Every plan includes the full LandlordOps OS: automated rent collection, Schedule E tax reporting, vendor management, ledger syncing, and Leo AI. Free (1-3 doors), Starter (15), Growth (50), and Scale (150) differ only by door count. Cancel anytime.`;
/** Full app title for meta and headers (non-home routes). */
export const APP_TITLE = `${APP_NAME} | ${TAGLINE}`;

/** Homepage document title (primary meta title). */
export const HOME_PAGE_TITLE =
  "LandlordOps | 24/7 AI Front Desk for Landlords";
/** Homepage meta name=description (search snippet). */
export const HOME_META_DESCRIPTION =
  "LandlordOps stops 2 AM tenant calls with Leo AI and gives you the full OS on every plan: rent collection, Schedule E, vendors, and ledgers. Plans differ only by door count. Protect your sleep and your asset.";
/** Open Graph / Twitter card title: emotional hook for shares (document title carries LandlordOps). */
export const HOME_OG_TITLE = "The End of the 2 AM Tenant Call";
/** Open Graph / Twitter card description: benefit-first for social previews. */
export const HOME_OG_DESCRIPTION =
  "Automate your tenant communication with Leo AI. Protect your sleep and build a verified Closing Vault for your property.";
/** OG image alt: high-intent phrases for accessibility and relevance. */
export const HOME_OG_IMAGE_ALT =
  "LandlordOps: full OS on every plan - rent collection, Schedule E, vendors, ledgers, and Leo AI; tiers differ by door count";
/**
 * OpsChat entry tier (Starter) for marketing copy. Keep in sync with getOpsChatIntakeTiers().
 */
const _opsChatEntryUsd =
  getOpsChatIntakeTiers("market_2026")[0]?.monthlyUSD ?? 19.99;
export const OPS_CHAT_MONTHLY_USD = _opsChatEntryUsd;
export const OPS_CHAT_PRICE_DISPLAY = `$${formatMonthlyUsd(
  _opsChatEntryUsd
)}/mo`;

/**
 * OpsChat / Free: max doors with tenant chat (Leo) on at one time. Portfolio may
 * have more doors up to the plan cap. Must match backend
 * `OPS_CHAT_MAX_CONCURRENT_TENANT_CHATS` in landlord-tier.service.
 */
export const OPS_CHAT_MAX_CONCURRENT_TENANT_CHATS = 3;

/** Homepage wedge pricing display (OpsChat entry); may differ from Stripe list for promos. */
export const HOME_WEDGE_OPSCHAT_MONTHLY_USD = 59;

/** Home page meta description; matches HOME_META_DESCRIPTION for Helmet. */
export const SEO_HOME_DESCRIPTION = HOME_META_DESCRIPTION;

export const APP_LOGO = "/logo.png";
export const APP_LOGO_TEXT = "/logo.png";

// URL Info
export const APP_URL = "landlordops.com";
/** Canonical HTTPS origin for JSON-LD, sitemaps, and AI-facing site summaries (no trailing slash). */
export const SITE_ORIGIN = "https://landlordops.com";

/**
 * Bump `OG_IMAGE_CACHE_VERSION` when replacing `public/meta.png`. Facebook, LinkedIn, and
 * X cache preview images by full URL; changing the file only is often not enough until you
 * also change the query string (or use their "scrape again" tools).
 */
export const OG_IMAGE_CACHE_VERSION = "3";
/** Default Open Graph / Twitter image (absolute URL). */
export const DEFAULT_OG_IMAGE_URL = `${SITE_ORIGIN}/meta.png?v=${OG_IMAGE_CACHE_VERSION}`;

// Email Info
export const APP_SUPPORT_EMAIL = `contact@${APP_URL}`;

// Service Info
export const PERSONA_TERM = "tenant";
export const adminUserId = "kKDOmz9HyQW6XwaufxZkvmnNeF02";

export const galleryPageLimit = 6;
export const commentLengthThreshold = 1;
export const commentLengthMax = 500;

export const spinnerIcon = "/icons/spinner.svg";

export const defaultArtQuestCard = "";
export const defaultWritingQuestCard = "";
export const defaultVoteCard = "";
export const defaultArtQuestFull = "";
export const defaultWritingQuestFull = "";
export const defaultVoteFull = "";

export const tikTokLink = `https://www.tiktok.com/@${APP_NAME_LOWER}`;
export const instagramLink = `https://www.instagram.com/${APP_NAME_LOWER}/`;
export const twitterLink = `https://twitter.com/use${APP_NAME_LOWER}`;
export const discordLink = "https://discord.gg/";
export const facebookLink = `https://www.facebook.com/${APP_NAME_LOWER}`;
export const linkedinLink = `https://www.linkedin.com/company/landlord-ops/ `;

export const datePattern = date.compile("ddd, MMM DD");
export const questCardDatePattern = date.compile("MMMM DD, YYYY");

export const noHeaderUiPaths = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/payment-success",
  "/payment-setup",
  "/pay-rent",
  "/tenant-chat",
  "/access-denied",
  "/token-expired",
  "/communication",
  "/communication/:tenantId",
  "/lease/", // Public listing pages (e.g. /lease/:listingId); trailing slash so /lease-ups is not matched
  "/apply/", // Public rental application (e.g. /apply/:listingId, /apply/thank-you)
  "/background-check/success",
  "/background-check/error",
  "/p/",
  "/negotiate",
  /** Footer only: global NavBar hidden, NavFooter (copyright) still shown */
  "/legacy",
  "/chat/",
];

export const noFooterUiPaths = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/payment-success",
  "/payment-setup",
  "/pay-rent",
  "/tenant-chat",
  "/access-denied",
  "/token-expired",
  "/communication",
  "/communication/:tenantId",
  "/background-check/success",
  "/background-check/error",
  "/p/",
  "/negotiate",
  "/chat/",
  "/apply/", // Public rental application: uses its own slim trust footer
];

export const noFirstTimeModalPaths = [
  "/",
  "/pay-rent",
  "/rent/tenant",
  "/communication",
  "/communication/:tenantId",
  "/tenant-chat/:tenantId",
  "/conversations",
  "/terms",
  "/privacy",
  "/payment-success",
  "/lease", // Public listing pages - don't show get started modal when landlord is viewing their listing
  "/apply", // Public rental application form / thank-you
  "/p/",
];

export const ERROR_PROFANITY =
  "Oops! Please use kind words. Let's keep our community positive and respectful ✌️";
export const usernameTakenErrorText = "That username is taken";
export const passwordErrorText = "Wrong password";

// The values here are used as keys in the Firebase table. Ye be warned when a-changing them.
export const configs = {
  likeYourComment: "like-your-comment",
  replyYourComment: "reply-your-comment",
  likeYourSubmission: "like-your-submission",
  replyYourSubmission: "reply-your-submission",
  newWebcomic: "new-webcomic",
  newEpisode: "new-episode",
  newQuest: "new-quest",
  questVotingLive: "quest-voting-live",
  resultsAnnounced: "results-announced",
  creatorPollLive: "creator-poll-live",
};

export const LOCAL_STORAGE = {
  hasAlerts: "has-alerts",
};
export const SESSION_STORAGE = {
  redirectUrl: "redirect-url",
};

export const ONE_DAY_FROM_NOW = new Date(
  new Date().getTime() + 60 * 60 * 24 * 1000
);

export const TWO_DAYS_FROM_NOW = new Date(
  new Date().getTime() + 60 * 60 * 48 * 1000
);

export const ALERT_AMOUNT_PER_PAGE = 15;

export const QUEST_TYPES = {
  VISUAL: "visual",
  WRITING: "writing",
  // TODO: remove after ai is gone
  IMAGE: "image",
};

export const SUBMISSION_TYPES = {
  IDEA: "idea",
  TEXT: "text",
  // TODO: remove after ai is gone
  GEN_AI: "image",
};

export const adminList = ["bryan@junoware.com"];

// Debug mode helpers for URL-based debug detection
// Simply checks if ?debug=true is in the URL (or just ?debug)
// Returns true if debug=true, debug=1, or just ?debug (without value)
// Returns false if debug=false, debug=0, or debug parameter is not present
export const getDebugMode = () => {
  if (typeof window === "undefined") return false;
  try {
    const urlParams = new URLSearchParams(window.location.search);

    // Check if debug parameter exists
    if (!urlParams.has("debug")) {
      return false; // Not present = false
    }

    const debugParam = urlParams.get("debug");

    // Explicitly false
    if (debugParam === "false" || debugParam === "0") {
      return false;
    }

    // Explicitly true, or just ?debug (without value = empty string or null)
    if (
      debugParam === "true" ||
      debugParam === "1" ||
      debugParam === "" ||
      debugParam === null
    ) {
      return true;
    }

    // Any other value = false
    return false;
  } catch (e) {
    return false;
  }
};

/** True when ?demo=true, ?demo=1, or ?demo (empty). Used for vendor bid demos (no real vendor email). */
export const getVendorBidDemoMode = () => {
  if (typeof window === "undefined") return false;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has("demo")) return false;
    const demoParam = urlParams.get("demo");
    if (demoParam === "false" || demoParam === "0") return false;
    if (
      demoParam === "true" ||
      demoParam === "1" ||
      demoParam === "" ||
      demoParam === null
    ) {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

/**
 * Mirrors backend DEMO_MODE for launch / whale-mode UI (dashboard ghost data).
 * Set REACT_APP_DEMO_MODE=true in the frontend env at build time.
 */
export const isLaunchDemoMode = () => {
  if (
    typeof process !== "undefined" &&
    process.env.REACT_APP_DEMO_MODE === "true"
  ) {
    return true;
  }
  return false;
};

/**
 * Set REACT_APP_DEPLOYMENT=sandbox in the build env (e.g. Netlify) for non-prod
 * marketing / preview deploys. Used to gate debug-only UI such as "Clear demo limits"
 * on the public Leo chat demo.
 */
export const isSandboxDeployment = () =>
  String(typeof process !== "undefined" ? process.env.REACT_APP_DEPLOYMENT : "")
    .trim()
    .toLowerCase() === "sandbox";

/**
 * With URL debug mode on /demo/bid-comparison, pass ?clean=true (or ?clean or ?clean=1)
 * to hide the preview ribbon and negotiation panel for marketing screenshots.
 */
export const getBidComparisonCleanScreenshot = () => {
  if (typeof window === "undefined") return false;
  try {
    const p = new URLSearchParams(window.location.search);
    if (!p.has("clean")) return false;
    const v = p.get("clean");
    if (v === "false" || v === "0") return false;
    if (v === "true" || v === "1" || v === "" || v === null) return true;
    return false;
  } catch (e) {
    return false;
  }
};

// Get debug unit count from URL parameter (defaults to 5)
export const getDebugUnitCount = () => {
  if (typeof window === "undefined") return 5;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const unitsParam = urlParams.get("units");
    if (unitsParam) {
      const count = parseInt(unitsParam, 10);
      // Limit to reasonable range (1-105 units)
      if (!isNaN(count) && count >= 1 && count <= 105) {
        return count;
      }
    }
    return 5; // Default
  } catch (e) {
    return 5;
  }
};

// Settings demo: which part of Settings to show with demo data.
// URL: ?debug=true&settings_demo=accounts | assets | both
// - accounts: show "Your deposit accounts" with multiple demo accounts (Primary + LLCs)
// - assets: show Door settings with multiple doors, some assigned to different deposit accounts
// - both: demo deposit accounts and demo assets with mixed assignments
export const getSettingsDemoMode = () => {
  if (typeof window === "undefined") return null;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has("settings_demo")) return null;
    const v = (urlParams.get("settings_demo") || "").toLowerCase();
    if (v === "accounts" || v === "assets" || v === "both") return v;
    return null;
  } catch (e) {
    return null;
  }
};

// Get custom name from URL parameter for Leo widget personalization
// Usage: ?name=John will make Leo say "Hey John! 👋"
export const getCustomName = () => {
  if (typeof window === "undefined") return null;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const nameParam = urlParams.get("name");
    if (nameParam && nameParam.trim().length > 0) {
      // Return the name with first letter capitalized
      return (
        nameParam.trim().charAt(0).toUpperCase() + nameParam.trim().slice(1)
      );
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const defaultProfileImages = [];

export function getItem(label, key, icon, children, type) {
  return {
    key,
    icon,
    children,
    label,
    type,
  };
}

export const generalQuillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      {
        color: [
          "#FFF6F2",
          "#EC55AB",
          "#F5A2FF",
          "#F6B438",
          "#EDE590",
          "#3DDE9C",
          "#4DADF9",
          "#7586FF",
        ],
      },
    ],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  },
  imageCompress: {
    quality: 0.5,
    maxWidth: 10000, // default
    maxHeight: 10000, // default
    // imageType: "image/jpeg", // default
    debug: false, // default
    suppressErrorLogging: false, // default
    insertIntoEditor: undefined, // default
    /* 
    insertIntoEditor: async (imageBase64URL, imageBlob) => {
      const downloadUrl = await uploadImage("questImages", imageBlob);
      console.log(downloadUrl);
      return imageBlob;
    },
    */
  },
  imageResize: {
    modules: ["Resize", "DisplaySize"],
    handleStyles: {
      backgroundColor: "white",
      border: "none",
      color: "white",
    },
  },
};

export const createPollQuillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      {
        color: [
          "#FFF6F2",
          "#EC55AB",
          "#F5A2FF",
          "#F6B438",
          "#EDE590",
          "#3DDE9C",
          "#4DADF9",
          "#7586FF",
        ],
      },
    ],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  },
  imageCompress: {
    quality: 0.8,
    maxWidth: 1000,
    maxHeight: 1000,
    imageType: "image/jpeg",
    debug: false,
    suppressErrorLogging: false,
    insertIntoEditor: undefined,
  },
  imageResize: {
    modules: ["Resize", "DisplaySize"],
    handleStyles: {
      backgroundColor: "white",
      border: "none",
      color: "white",
    },
  },
};

export const createCreatorPollQuillModules = {
  toolbar: [["bold", "italic", "underline", "emoji", { list: "bullet" }]],
  "emoji-toolbar": true,
  "emoji-shortname": true,
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  },
};

export const vhPanelLeftPanelWidth = 360;
export const vhPanelrightPanelLeftMargin = 10;

export const storyworldTypes = {
  ARCHIVED: "archived",
  CATALOG: "catalog",
  STORYWORLD: "storyworld",
};

// dropdown is set by antd currently at 1050
export const zIndex = {
  sheet: 999,
  authSheet: 1000,
};

export const maxIdeaBlockEntries = 3;

export const PROMO_STATUS = {
  ACTIVE: "active",
  EXPIRED: "expired",
  SCHEDULED: "scheduled",
};

export const getPromoStatus = (promo) => {
  if (!promo) return "";

  const start = promo.startTimestamp;
  const end = promo.endTimestamp;

  if (start < Date.now() && end > Date.now()) {
    return PROMO_STATUS.ACTIVE;
  } else if (end < Date.now()) {
    return PROMO_STATUS.EXPIRED;
  }

  return PROMO_STATUS.SCHEDULED;
};

export const getPromoStatusColor = (promo) => {
  const status = getPromoStatus(promo);

  if (status === PROMO_STATUS.ACTIVE) {
    return "#70F2BA";
  } else if (status === PROMO_STATUS.EXPIRED) {
    return "var(--color-red-error)";
  }

  return "#D9D9D9";
};

export const MIN_USERNAME_LENGTH = 3;

// Define the available unit types for landlords
export const unitTypes = [
  {
    value: "apartment",
    label: "Apartment",
    buttonText: "Apartment",
  },
  { value: "house", label: "House", buttonText: "House" },
  { value: "condo", label: "Condo", buttonText: "Condo" },
  {
    value: "townhouse",
    label: "Townhouse",
    buttonText: "Townhouse",
  },
  { value: "duplex", label: "Duplex", buttonText: "Duplex" },
  {
    value: "studio",
    label: "Studio",
    buttonText: "Studio",
  },
  {
    value: "loft",
    label: "Loft",
    buttonText: "Loft",
  },
  {
    value: "mobile-home",
    label: "Mobile Home",
    buttonText: "Mobile Home",
  },
  {
    value: "commercial",
    label: "Commercial",
    buttonText: "Commercial",
  },
  {
    value: "multi-family",
    label: "Multi-Family",
    buttonText: "Multi-Family",
  },
];

// Define US states for lease templates
export const usStates = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];
